import { useEffect } from "react";

/**
 * Reusable effect for custom selects that calls a callback when an element becomes focused
 * that is not a child of the select so you can manually close the select dropdown
 *
 * @param {react Ref} parentRef - react reference to the select itself
 * @param {react Ref} dropdownRef - react reference to the dropdown that appears when opening the select
 * @param {function} onBlurUpdate - callback when blur occurs to an element not contained in the select
 * @param {string} nestedTag - used to specify HTML tag of focusable nested element in the select dropdown
 */
export function useDropdownFocus(
  parentRef,
  dropdownRef,
  onBlurUpdate,
  nestedTag,
) {
  useEffect(() => {
    const onBlur = ({ relatedTarget }) => {
      // check if the newly focused element is the selector or one of it's children
      if (
        relatedTarget &&
        !(dropdownRef.current && dropdownRef.current.contains(relatedTarget))
      ) {
        // Hack fix for safari as focusout gets called before click
        setTimeout(onBlurUpdate, 100);
      }
    };

    // Must use focusout event for ie 11
    parentRef.current.addEventListener("focusout", onBlur);
    const options = !dropdownRef.current
      ? []
      : nestedTag
      ? dropdownRef.current.getElementsByTagName(nestedTag)
      : dropdownRef.current.children;

    // options is an HTMLCollection, for...of not supported in IE
    for (let i = 0; i < options.length; i++) {
      options[i].addEventListener("focusout", onBlur);
    }
    // Need to store this in local var as parentRef will likely have changed when the cleanup runs
    const parentCurrent = parentRef.current;
    return () => {
      parentCurrent.removeEventListener("focusout", onBlur);
      for (let i = 0; i < options.length; i++) {
        options[i].removeEventListener("focusout", onBlur);
      }
    };
  }, [dropdownRef, nestedTag, onBlurUpdate, parentRef]);
}
